import { useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Mode } from "../../types";
import { useUserQuery } from "../queries/useUserQuery";
import { useUIStore, useTutorChatStore } from "../../store";
import { useMediaMd } from "../../../../hooks/useMedia";
import { useCreateGroupMutation } from "../queries/mutations/useGroupMutations";
import { usePressCardQuery } from "../../../myAirV2/hooks/queries/usePressCardQuery";
import getUserState from "../../utils/getUserState";

/**
 * useMenuView
 *
 * Menu 컴포넌트를 위한 presentation 훅 (단순화)
 *
 * 기능:
 * - 새 채팅 생성 (handleNewChat)
 * - 모드 전환 (handleToggleMode)
 * - UI 상태 관리 (openNav, isDialogMenu, etc.)
 *
 * 참고:
 * - 그룹 목록/보관함 목록은 GroupMenuList/StorageMenuList에서 직접 처리
 */

interface UseMenuViewOptions {
  mode: Mode;
}

const useMenuView = (options: UseMenuViewOptions) => {
  const { mode } = options;

  // Mutation Hook으로 새 채팅 생성
  const { pageName } = useUIStore();
  const createGroupMutation = useCreateGroupMutation(mode, pageName);

  const { user, isLoggedIn } = useUserQuery();
  // 로그인 상태일 때 기자증 발급 상태를 조회한다. (userId = profile_id)
  const { isPressCardValid } = usePressCardQuery(user?.id ?? null, isLoggedIn);
  const { openNav, openUserPopover, setOpenUserPopover, toggleNav, closeNav } =
    useUIStore();
  const { fetching } = useTutorChatStore();
  const navigate = useNavigate();
  const location = useLocation();
  const isMd = useMediaMd();

  const isDialogMenu = !isMd;
  const userState = getUserState(user);

  // 새 채팅 생성 핸들러
  const handleNewChat = useCallback(async () => {
    // 스트리밍 중이면 새 채팅 생성 방지
    if (fetching) return;

    // 소크라테스 모드: 소크라테스 만들기 페이지로 이동
    if (mode === Mode.SOCRATES) {
      navigate("/socrates/new");
      // 모바일에서 사이드바 닫기
      if (!isMd) closeNav();
      return;
    }

    // 튜터 모드: 기존 로직 (새 그룹 생성)
    await createGroupMutation.mutateAsync({
      title: "새 대화",
    });
    // 모바일에서 사이드바 닫기
    if (!isMd) closeNav();
  }, [createGroupMutation, fetching, mode, navigate, isMd, closeNav]);

  /**
   * 모드 전환 핸들러
   */
  const handleToggleMode = useCallback(
    (newMode: Mode) => {
      let currentPage = "";

      if (location.pathname === "/" || location.pathname === "/tutor")
        currentPage = "tutor";
      else if (location.pathname === "/socrates") currentPage = "socrates";
      else currentPage = "other";

      if (newMode === Mode.SOCRATES && currentPage !== "socrates") {
        navigate("/socrates");
      } else if (newMode === Mode.TUTOR && currentPage !== "tutor") {
        navigate("/tutor");
      }
    },
    [location.pathname, navigate]
  );

  const handleClickMore = useCallback(() => {
    navigate("/my-air");
  }, [navigate]);

  const handlePurchaseClick = () => {};

  // 기자증 배너 클릭 (팝플 인앱 브라우저)
  //   - 발급 완료 + 유효(isPressCardValid): 팝플 기자증 페이지로 이동
  //   - 미발급 / 만료                      : 팝플 메인으로 이동
  const handlePressCardClick = useCallback(() => {
    const planet =
      process.env.REACT_APP_POPCORN_HOST || "https://www.popcornplanet.co.kr";
    const url = isPressCardValid ? `${planet}/mypage/press-card` : planet;
    window.open(url, "_blank");
    // 모바일에서 사이드바 닫기
    if (!isMd) closeNav();
  }, [isPressCardValid, isMd, closeNav]);

  return {
    // 상태
    user,
    openNav,
    isDialogMenu,
    openUserPopover,
    userState,
    fetching,
    isPressCardValid,

    // 액션
    handleNewChat,
    handleToggleMode,
    handleClickMore,
    toggleNav,
    setOpenUserPopover,
    handlePurchaseClick,
    handlePressCardClick,
  };
};

export default useMenuView;
